import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Stack, IconButton, Typography } from '@mui/material';
import { selectEffectiveLimit } from '../../store/actions/licenseActions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Dashboard from '../../components/containers/Dashboard/Dashboard';
import AddWidgetButton from '../AddWidgetButton/AddWidgetButton';
import SaveButton from '../SaveButton/SaveButton';
import PublishButton from '../PublishButton/PublishButton';
import ViewVersionButton from '../ViewVersionButton/ViewVersionButton';
import VersionsButton from '../VersionsButton/VersionsButton';
import SettingsButton from '../SettingsButton/SettingsButton';
import UndoRedoButtons from '../UndoRedoButtons/UndoRedoButtons';
import Toast from '../../dummies/Toast/Toast';
import { CenterDialog } from '../../dummies/CenterDialog/CenterDialog';
import BoardOnboarding from '../BoardOnboarding/BoardOnboarding';

const ONBOARDING_KEY = 'inorai_board_onboarding_v1';

/**
 * DashboardEditor - עורך דשבורד עם כפתורי פעולה
 * מעטף את קומפוננטת Dashboard הרגילה ומוסיף שורת כפתורים למעלה
 * 
 * Business Component - הכפתורים הם סטנד-אלון ומטפלים בעצמם ב-Redux
 * 
 * משמש במצב עריכה בלבד (/:organizationId/dashboard/:dashboardId)
 * 
 * @param {object} user - משתמש מחובר
 * @param {object} organization - ארגון
 * @param {object} dashboard - דשבורד
 * @param {boolean} hasUnsavedChanges - האם יש שינויים שלא נשמרו
 * @param {boolean} isSaving - האם בתהליך שמירה
 * @param {function} dispatch - Redux dispatch
 * @param {array} catalog - קטלוג הווידג'טים
 * @param {array} history - היסטוריית פעולות
 * @param {number} historyIndex - מיקום נוכחי בהיסטוריה
 * @param {array} myWidgets - כל הווידג'טים מכל הדשבורדים
 * @param {array} savedWidgets - רכיבים שמורים ברמת ארגון
 */
const DashboardEditor = ({
    user,
    organization,
    dashboard,
    hasUnsavedChanges,
    isSaving,
    dispatch,
    catalog,
    history = [],
    historyIndex = -1,
    myWidgets = [],
    savedWidgets = [],
    templateMode = false,
    onBack = null,
    extraActions = null
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const fromScreenId = location.state?.fromScreenId;
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);

    const handleOnboardingClose = () => {
        localStorage.setItem(ONBOARDING_KEY, '1');
        setShowOnboarding(false);
    };

    // State לשמירת handlers מ-Dashboard
    const [dashboardHandlers, setDashboardHandlers] = useState(null);

    const dashboardId = dashboard?.dashboardId;
    const versions = dashboard?.versions || [];
    const latestVersion = versions.length > 0 ? versions[versions.length - 1] : null;
    const versionLimit = useSelector(state => selectEffectiveLimit(state, 'versionHistory'));

    const handleCloseToast = () => {
        setToast({ ...toast, open: false });
    };

    const handleSaveSuccess = () => {
        setToast({ open: true, message: 'הדשבורד נשמר בהצלחה!', severity: 'success' });
    };

    const handleSaveError = (errorMessage) => {
        setToast({ open: true, message: errorMessage, severity: 'error' });
    };

    const handlePublishSuccess = () => {
        setToast({ open: true, message: 'הגירסה פורסמה בהצלחה!', severity: 'success' });
    };

    const handlePublishError = (errorMessage) => {
        setToast({ open: true, message: errorMessage, severity: 'error' });
    };

    const handleViewVersion = () => {
        if (!latestVersion || !organization?.organizationId || !dashboardId) return;
        window.open(`/${organization.organizationId}/view/${dashboardId}`, '_blank');
    };

    const handleLoadVersion = (version) => {
        // הכפתור זורק את ה-action בעצמו
        // רק מקבל callback לטוסט
        setToast({ open: true, message: 'הגירסה נטענה לטיוטה. לחץ "שמור" כדי לשמור את השינויים.', severity: 'info' });
    };

    const handleBackClick = () => {
        // בדיקה אם יש שינויים שלא נשמרו
        if (hasUnsavedChanges) {
            setUnsavedChangesDialogOpen(true);
        } else {
            handleBackConfirm();
        }
    };

    const handleBackConfirm = () => {
        setUnsavedChangesDialogOpen(false);
        if (templateMode && onBack) {
            onBack();
        } else if (fromScreenId && organization?.organizationId) {
            navigate(`/${organization.organizationId}/screens/${fromScreenId}`);
        } else {
            navigate(`/${organization?.organizationId}/screens`);
        }
    };

    const handleBackCancel = () => {
        setUnsavedChangesDialogOpen(false);
    };

    // Undo/Redo handlers - לא צריך יותר, הכפתורים מטפלים בעצמם

    // הגנה מפני רענון דף עם שינויים לא שמורים
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = ''; // Chrome דורש את זה
                return ''; // דפדפנים אחרים
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);



    const currentBgColor = dashboard?.current?.settings?.bgColor || '#f0ece6';
    const currentBgImage = dashboard?.current?.settings?.bgImage || '';
    const currentBorderRadius = dashboard?.current?.settings?.borderRadius ?? 35;
    const currentFontFamily = dashboard?.current?.settings?.fontFamily || 'default';
    const currentWidgetShadow = dashboard?.current?.settings?.widgetShadow || 'none';
    const currentWidgetShadowColor = dashboard?.current?.settings?.widgetShadowColor || '#000000';
    //  if (isPublicView && dashboard) {
    //     return <div style={{ position: 'relative', direction: 'ltr', width: '100%', height: '100vh', marginTop: '50px'}}>

    //       <Dashboard user={user} organization={organization} onLogout={handleLogout} dispatch={dispatch} hasUnsavedChanges={hasUnsavedChanges} isSaving={isSaving} />;
    //     </div>
    //   }
    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            bgcolor: '#fcfbfa',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* שורת כפתורים עליונה */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 100,
                width: '100%',
                background: 'linear-gradient(135deg, rgba(30,58,95,0.04) 0%, rgba(255,255,255,0) 70%)',
                bgcolor: 'white',
                borderBottom: '1px solid rgba(30,58,95,0.09)',
                px: 4,
                pt: 1.5,
                pb: 0.5,
                boxShadow: '0 2px 12px rgba(30,58,95,0.07)'
            }}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                    {/* כפתור חזרה - תמיד מוצג */}
                    <IconButton
                        onClick={handleBackClick}
                        sx={{ color: '#1e3a5f' }}
                        title={fromScreenId ? 'חזרה למסך' : 'חזרה לרשימת מסכים'}
                    >
                        <ArrowForwardIcon />
                    </IconButton>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>
                            {dashboard?.name || 'דשבורד'}
                        </Typography>
                        {dashboard?.description && (
                            <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                {dashboard.description}
                            </Typography>
                        )}
                    </Box>

                    {/* שאר הכפתורים */}
                    <AddWidgetButton
                        onSelectWidget={dashboardHandlers?.handleSelectWidget || (() => { })}
                        onCopyWidget={dashboardHandlers?.handleCopyWidget || (() => { })}
                        onMoveWidget={dashboardHandlers?.handleMoveWidget || (() => { })}
                        onOpenWidgetConfig={dashboardHandlers?.handleOpenWidgetConfig || (() => { })}
                        catalog={catalog || []}
                        myWidgets={myWidgets}
                        savedWidgets={savedWidgets}
                        currentDashboardId={dashboardId}
                    />

                    {/* Undo/Redo Buttons */}
                    <UndoRedoButtons
                        dispatch={dispatch}
                        historyIndex={historyIndex}
                        historyLength={history.length}
                    />

                    {templateMode ? (
                        extraActions
                    ) : (
                        <>
                            <SaveButton
                                dashboardId={dashboardId}
                                dashboard={dashboard}
                                hasUnsavedChanges={hasUnsavedChanges}
                                dispatch={dispatch}
                                organizationId={organization?.organizationId}
                                savedWidgets={savedWidgets}
                                onSuccess={handleSaveSuccess}
                                onError={handleSaveError}
                            />
                            <PublishButton
                                dashboardId={dashboardId}
                                hasUnsavedChanges={hasUnsavedChanges}
                                dispatch={dispatch}
                                onSuccess={handlePublishSuccess}
                                onError={handlePublishError}
                            />
                            <ViewVersionButton
                                onView={handleViewVersion}
                                disabled={!latestVersion}
                                fullWidth={false}
                            />
                            <VersionsButton
                                onLoadVersion={handleLoadVersion}
                                versions={versions}
                                disabled={false}
                                organization={organization}
                                catalog={catalog}
                                dispatch={dispatch}
                                versionLimit={versionLimit}
                            />
                        </>
                    )}

                    {/* Settings Button - הכי שמאלה */}
                    <SettingsButton
                        bgColor={currentBgColor}
                        bgImage={currentBgImage}
                        borderRadius={currentBorderRadius}
                        fontFamily={currentFontFamily}
                        widgetShadow={currentWidgetShadow}
                        widgetShadowColor={currentWidgetShadowColor}
                        dispatch={dispatch}
                    />

                    {/* Help / replay onboarding */}
                    <IconButton
                        onClick={() => setShowOnboarding(true)}
                        title="הצג הדרכה"
                        sx={{ color: '#1e3a5f', opacity: 0.6, '&:hover': { opacity: 1 } }}
                    >
                        <HelpOutlineIcon />
                    </IconButton>
                </Stack>
                <Typography variant="caption" sx={{
                    display: 'block',
                    textAlign: 'left',
                    direction: 'rtl',
                    color: '#1e3a5f',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    pt: '3px',
                    ml: 11.5,
                    '& strong': { fontWeight: 700 }
                }}>
                    שינויים בלוח ובהגדרות הרכיבים לא נשמרים עד לחיצה על <strong>שמור</strong>, ואינם גלויים לציבור עד לחיצה על <strong>פרסום גרסה</strong>
                </Typography>
            </Box>

            {/* Dashboard Container עם מסגרת יפה */}
            <Box sx={{
                flex: 1,
                p: 4,
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Box sx={{
                    flex: 1,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 'calc(100vh - 140px)',

                }}>
                    {/* Dashboard במצב עריכה */}
                    <Box sx={{
                        flex: 1,
                        overflow: 'auto',
                        position: 'relative',
                        direction: 'ltr'
                    }}>
                        <Dashboard
                            user={user}
                            organization={organization}
                            dashboard={templateMode ? dashboard : undefined}
                            onLogout={() => { }}
                            dispatch={dispatch}
                            hasUnsavedChanges={hasUnsavedChanges}
                            isSaving={isSaving}
                            isEditMode="editor"
                            onHandlersReady={setDashboardHandlers}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Toast Notifications */}
            <Toast
                open={toast.open}
                message={toast.message}
                severity={toast.severity}
                onClose={handleCloseToast}
            />

            {/* Board Onboarding Overlay */}
            {showOnboarding && <BoardOnboarding onClose={handleOnboardingClose} />}

            {/* Unsaved Changes Dialog */}
            <CenterDialog
                open={unsavedChangesDialogOpen}
                onClose={handleBackCancel}
                title="שינויים לא נשמרו"
                message="יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לצאת?"
                confirmText="צא בלי לשמור"
                cancelText="המשך לערוך"
                onConfirm={handleBackConfirm}
                severity="warning"
            />
        </Box>
    );
};

export default DashboardEditor;
